import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import SectionRecord from "models/view-models/section-record";
import React, { useMemo } from "react";
import { PropsWithChildren } from "react";
import { t } from "utilities/localization-utils";
import HighlightModal from "organisms/highlight-modal/highlight-modal";
import { HighlightProperties } from "./code-reference-preview";
import { HighlightColor } from "utilities/enumerations/highlight-color";
import useActiveHighlightModal from "organisms/highlight-modal/useActiveHighlightModal";
import useModalActions from "utilities/hooks/use-modal-actions";
import { BrowserUtils } from "utilities/browser-utils";
import HighlightUtils from "utilities/highlight-utils";
import EnhancedContentPanel from "organisms/enhanced-content/enhanced-content-panel";
import useNotebooksEnhancedContent from "utilities/hooks/domain/enhanced-content/use-notebooks-enhanced-content";

const CSS_CLASS_NAME = "c-code-reference-block-content";
interface CodeReferenceBlockSectionWrapperProps extends PropsWithChildren {
    section: SectionRecord | null;
    cssClassName?: string;
    onSaveHighlight: (props: HighlightProperties) => void;
    onSelectionEnd?: () => void;
    isEditMode: boolean;
    showButton?: boolean;
    onButtonClick: () => void;
    onCancel: () => void;
    onColorChange: (newColor: HighlightColor) => void;
    isEnhancedContentDisplayed?: boolean;
    printable?: boolean;
}
const CodeReferenceBlockSectionWrapper = ({
    section,
    children,
    onSaveHighlight,
    showButton = false,
    onSelectionEnd,
    isEditMode,
    cssClassName = "",
    onButtonClick,
    onCancel,
    onColorChange,
    isEnhancedContentDisplayed,
    printable = false,
}: CodeReferenceBlockSectionWrapperProps) => {
    const {
        setActiveEditModal,
        closeActiveEditModal,
        activeEditHighlightModal,
    } = useActiveHighlightModal();
    const { handleClose, handleOpen, isOpen: renderModal } = useModalActions();
    const isMouseDevice = useMemo(() => BrowserUtils.isMouseDevice(), []);
    const isButtonVisible = isEditMode && showButton && isMouseDevice;

    const handleButtonClick = () => {
        onButtonClick();
        setActiveEditModal("editClick");
        handleOpen();
    };

    const handleCancel = () => {
        onCancel();
        handleClose();
        closeActiveEditModal();
    };

    const handleSave = (props: HighlightProperties) => {
        onSaveHighlight(props);
        closeActiveEditModal();
        handleClose();
    };

    const handleSelectionEnd = (e) => {
        HighlightUtils.normalizeRange();
        onSelectionEnd && onSelectionEnd();
    };
    const {
        enhancedContentForCurrentCaapsItem,
        enhancedContentResourcesForCurrentCaapsItem,
    } = useNotebooksEnhancedContent({
        externalId: section?.externalId,
    });
    return (
        <div
            className={`${CSS_CLASS_NAME}__section-wrapper ${cssClassName}`}
            onMouseUp={handleSelectionEnd}
            onTouchEnd={handleSelectionEnd}
            data-section-id={section?.id}
            id={`code-wrap${section?.id}`}>
            <div
                data-no-highlight
                className={`${CSS_CLASS_NAME}__button-wrapper`}>
                <Button
                    ariaLabel={t("highlight")}
                    disabled={!isButtonVisible}
                    cssClassName={`${CSS_CLASS_NAME}__highlight-button ${isButtonVisible ? "" : "-hidden"}`}
                    size={ButtonSizes.XSmall}
                    style={ButtonStyles.None}
                    onClick={handleButtonClick}
                    data-no-highlight={true}>
                    <Icon type={Icons.Highlighter} />
                </Button>
            </div>
            {children}
            {enhancedContentForCurrentCaapsItem &&
                isEnhancedContentDisplayed && (
                    <div
                        className={`${CSS_CLASS_NAME}__enhanced-content-wrapper`}
                        data-no-highlight>
                        <EnhancedContentPanel
                            alwaysShow
                            isPrintView={printable}
                            hideAdminTogglesFromAdmins
                            resources={
                                enhancedContentResourcesForCurrentCaapsItem
                            }
                            enhancedContent={enhancedContentForCurrentCaapsItem}
                        />
                    </div>
                )}
            {activeEditHighlightModal === "editClick" && renderModal && (
                <HighlightModal
                    onSave={handleSave}
                    onClose={handleCancel}
                    onColorChange={onColorChange}
                />
            )}
        </div>
    );
};

export default CodeReferenceBlockSectionWrapper;
